// native
import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';

// addon
import { moveItemInArray, CdkDragEnter } from '@angular/cdk/drag-drop';
import { ContextMenuComponent, ContextMenuService } from '@readcube/ngx-contextmenu';

// service
import { CitationsEventService } from '../../../core/services/citations-event.service';
import { ItemsDisplayService } from '../../../core/services/items-display.service';
import { CitationsQueueService } from '../../../core/services/citations-queue.service';
import { UrlService } from '../../../core/services/url.service';
import { UtilityService } from '../../../core/services/utility.service';

// model
import { IDisplayItem, ICitationSelection } from '../../../models';

// constants
import { DRAWER_ELEMENT_HEIGHT, LOADER_ELEMENT_HEIGHT } from '../../../constants/constants';

@Component({
  selector: 'scs-citations-queue',
  templateUrl: './citations-queue.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CitationsQueueComponent implements OnInit, OnDestroy {

  @ViewChild('contextMenuComponent', { static: true }) contextMenuComponent: ContextMenuComponent;

  @ViewChild('citationsContainer') citationsContainerElement: ElementRef;

  items: IDisplayItem[] = [];

  citationQueueLoadingSubscription: Subscription;
  citationSelectedSubscription: Subscription;
  clearCitationQueueSubscription: Subscription;
  itemAddedToCitationQueueSubscription: Subscription;
  itemUncheckedFromListSubscription: Subscription;

  constructor(
    public citationsEventService: CitationsEventService,
    private citationsQueueService: CitationsQueueService,
    private changeDetectorRef: ChangeDetectorRef,
    public itemsDisplayService: ItemsDisplayService,
    public urlService: UrlService,
    private contextMenu: ContextMenuService,
    private utilityService: UtilityService
  ) { }

  ngOnInit(): void {
    this.initCitationSubscriptions();
  }

  private initCitationSubscriptions() {
    this.citationQueueLoadingSubscription = this.citationsEventService.citationQueueLoading$.subscribe((isLoading: boolean) => {
      isLoading && this.citationsEventService.citationContainerOffsetHeight$.next(LOADER_ELEMENT_HEIGHT);
    });

    this.citationSelectedSubscription = this.citationsEventService.citationSelected$.subscribe((selection: ICitationSelection) => {
      if (selection.isCitationDeselected || !!selection.items) {
        this.items = selection.items || [];
        this.citationsQueueService.citationQueueOptions = selection.options;
        this.citationsQueueService.citationQueueItems = this.items;
        this.updateContainerHeight();
        this.changeDetectorRef.markForCheck();
      }
    });

    this.itemAddedToCitationQueueSubscription = this.citationsEventService.itemAddedToCitationQueue$.subscribe((item: IDisplayItem) => {
      this.items.push(item);
      this.citationsQueueService.citationQueueItems = this.items;
      this.updateContainerHeight();
      this.changeDetectorRef.markForCheck();
    });

    this.itemUncheckedFromListSubscription = this.citationsEventService.itemUncheckedFromList$.subscribe((item: IDisplayItem) => {
      this.removeItem(item);
    });

    this.clearCitationQueueSubscription = this.citationsEventService.clearCitationQueue$.subscribe(() => {
      this.items = [];
      this.citationsQueueService.citationQueueItems = [];
      this.citationsQueueService.citationQueueOptions = null;
      this.updateContainerHeight();
      this.changeDetectorRef.markForCheck();
    });
  }

  removeItem(item: IDisplayItem, propagateRemoval = false) {
    this.items = this.items.filter((i: IDisplayItem) => {
      return i.id !== item.id;
    });

    this.citationsQueueService.citationQueueItems = this.items;
    !this.items.length && this.citationsEventService.clearCitationQueue$.next();
    this.updateContainerHeight();

    this.changeDetectorRef.markForCheck();

    propagateRemoval && this.citationsEventService.itemRemovedFromCitationQueue$.next(item);
  }

  private updateContainerHeight() {
    // done in next thick because ui element is still not renedered
    setTimeout(() => {
      (<HTMLElement>this.citationsContainerElement.nativeElement).scrollTop = (<HTMLElement>this.citationsContainerElement.nativeElement).scrollHeight;

      const citationsElementHeight = (<HTMLElement>this.citationsContainerElement.nativeElement).clientHeight;
      const offsetHeight = citationsElementHeight ? (citationsElementHeight + DRAWER_ELEMENT_HEIGHT) : citationsElementHeight;

      this.citationsEventService.citationContainerOffsetHeight$.next(offsetHeight);
    });
  }

  drop(event: CdkDragEnter) {
    moveItemInArray(this.items, event.item.data, event.container.data);
  }

  onOverrideClick() {
    this.citationsQueueService.overridePanelVisible$.next(true);
  }

  ngOnDestroy() {
    this.citationsQueueService.citationQueueItems = [];
    this.updateContainerHeight();
    this.citationSelectedSubscription?.unsubscribe();
    this.citationQueueLoadingSubscription?.unsubscribe();
    this.itemAddedToCitationQueueSubscription?.unsubscribe();
    this.itemUncheckedFromListSubscription?.unsubscribe();
    this.clearCitationQueueSubscription?.unsubscribe();
  }

  isLibraryItem(item: IDisplayItem): boolean {
    return !!item.collection_id && !!item.id;
  }

  hasPdf(item: IDisplayItem): boolean {
    return item?.primary_file_type === 'pdf';
  }

  getYear(item: IDisplayItem) {
    return this.utilityService.getYearFromMetadata(item.custom_metadata?.date) || 'Unknown year';
  }

  openContextMenu(event: MouseEvent, item: IDisplayItem) {
    this.contextMenu.show.next({
      contextMenu: this.contextMenuComponent,
      anchorElement: event.currentTarget,
      event: event,
      item
    });
    event.stopPropagation();
    event.preventDefault();
  }
}
